
import { getCommonPrintStyles } from "../printStyles";

/**
 * Generate CSS styles for student certificate
 */
export const getCertificateStyles = (): string => {
  return `
    ${getCommonPrintStyles()}
    
    /* Import modern fonts */
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
    
    /* PORTRAIT ORIENTATION - ENFORCED */
    @page {
      size: A4 portrait !important;
      margin: 0.2in !important;
    }
    
    html, body {
      width: 100% !important;
      height: 100% !important;
      orientation: portrait !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .certificate-page {
      width: 100% !important;
      min-height: 100vh !important;
      max-width: 850px !important;
      margin: 0 auto !important;
      display: flex !important;
      justify-content: center !important;
      align-items: stretch !important;
      background: white !important;
      font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif !important;
      padding: 4px !important;
      box-sizing: border-box !important;
      orientation: portrait !important;
    }
    
    .certificate-border {
      width: 100% !important;
      max-width: 800px !important;
      height: auto !important;
      min-height: 950px !important;
      background: white !important;
      border-radius: 20px !important;
      display: flex !important;
      flex-direction: column !important;
      position: relative !important;
      margin: 0 auto !important;
      
      /* ULTRA PROFESSIONAL GOLDEN BORDER WITH ENHANCED EFFECTS - FIXED */
      border: 8px solid #d4af37 !important;
      background-color: white !important;
      background-clip: padding-box !important;
      
      /* ENHANCED PROFESSIONAL SHADOW EFFECTS - SIMPLIFIED FOR BETTER RENDERING */
      box-shadow: 
        0 0 0 2px rgba(212, 175, 55, 0.6) !important,
        0 0 0 4px rgba(212, 175, 55, 0.4) !important,
        0 0 0 6px rgba(212, 175, 55, 0.2) !important,
        0 8px 32px rgba(212, 175, 55, 0.3) !important,
        0 12px 48px rgba(0, 0, 0, 0.15) !important;
      
      /* PROFESSIONAL BORDER GLOW EFFECT */
      filter: drop-shadow(0 0 15px rgba(212, 175, 55, 0.25)) !important;
      
      /* Ensure border is always visible */
      outline: 2px solid #d4af37 !important;
      outline-offset: -2px !important;
    }
    
    /* ENHANCED DECORATIVE CORNER ELEMENTS - FIXED POSITIONING */
    .certificate-border::before,
    .certificate-border::after {
      content: '' !important;
      position: absolute !important;
      width: 80px !important;
      height: 80px !important;
      border: 3px solid #d4af37 !important;
      z-index: 20 !important;
      background: rgba(212, 175, 55, 0.1) !important;
      box-shadow: 
        inset 0 0 15px rgba(212, 175, 55, 0.2),
        0 0 10px rgba(212, 175, 55, 0.15) !important;
    }
    
    .certificate-border::before {
      top: 20px !important;
      left: 20px !important;
      border-right: none !important;
      border-bottom: none !important;
      border-top-left-radius: 12px !important;
    }
    
    .certificate-border::after {
      bottom: 20px !important;
      right: 20px !important;
      border-left: none !important;
      border-top: none !important;
      border-bottom-right-radius: 12px !important;
    }
    
    /* INNER BORDER FOR EXTRA DEFINITION */
    .certificate-border {
      box-shadow: 
        inset 0 0 0 4px rgba(212, 175, 55, 0.15),
        0 0 0 2px rgba(212, 175, 55, 0.6),
        0 0 0 4px rgba(212, 175, 55, 0.4),
        0 0 0 6px rgba(212, 175, 55, 0.2),
        0 8px 32px rgba(212, 175, 55, 0.3),
        0 12px 48px rgba(0, 0, 0, 0.15) !important;
    }

    /* ... keep existing code (all other certificate styles) */
    
    .certificate-number-container {
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      margin-top: 15px !important;
      width: 100% !important;
    }
    
    .certificate-number {
      font-family: 'Inter', monospace !important;
      font-size: 11px !important;
      color: #000000 !important;
      background: white !important;
      padding: 8px 18px !important;
      border-radius: 15px !important;
      border: 2px solid #d4d4d8 !important;
      text-align: center !important;
      font-weight: 600 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
      letter-spacing: 0.5px !important;
    }

    /* HEADER - CLEAN STYLING */
    .certificate-header-inside {
      background: white !important;
      color: #1e293b !important;
      padding: 30px 25px 20px 25px !important;
      flex-shrink: 0 !important;
      text-align: center !important;
      position: relative !important;
      z-index: 5 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .header-content-centered {
      text-align: center !important;
      width: 100% !important;
    }
    
    .institution-name-main {
      font-family: 'Playfair Display', 'Georgia', serif !important;
      font-size: 36px !important;
      font-weight: 700 !important;
      margin-bottom: 8px !important;
      color: #1e293b !important;
      letter-spacing: 1px !important;
      text-shadow: none !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .certificate-subtitle-header {
      font-family: 'Inter', sans-serif !important;
      font-size: 18px !important;
      color: #64748b !important;
      font-weight: 500 !important;
      letter-spacing: 1.5px !important;
      text-transform: uppercase !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* MAIN CONTENT AREA */
    .certificate-main {
      flex: 1 !important;
      padding: 20px 30px 15px 30px !important;
      text-align: center !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: flex-start !important;
      min-height: 400px !important;
      margin: 0 auto !important;
      width: 100% !important;
      box-sizing: border-box !important;
      background: white !important;
      position: relative !important;
      z-index: 5 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .generation-date {
      font-family: 'Inter', sans-serif !important;
      font-size: 12px !important;
      color: #64748b !important;
      text-align: right !important;
      margin-bottom: 10px !important;
      font-style: italic !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .certificate-title {
      font-family: 'Playfair Display', serif !important;
      font-size: 28px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      letter-spacing: 3px !important;
      margin-bottom: 10px !important;
      text-transform: uppercase !important;
      text-shadow: none !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .certificate-subtitle-main {
      font-family: 'Crimson Text', serif !important;
      font-size: 16px !important;
      color: #475569 !important;
      font-style: italic !important;
      margin-bottom: 12px !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .student-info-section {
      margin: 12px 0 !important;
    }
    
    .student-name-certificate {
      font-family: 'Playfair Display', serif !important;
      font-size: 28px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      border-bottom: 3px solid #e2e8f0 !important;
      display: inline-block !important;
      padding: 8px 25px !important;
      margin-bottom: 10px !important;
      text-shadow: none !important;
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .student-father-name {
      font-family: 'Inter', sans-serif !important;
      font-size: 14px !important;
      color: #64748b !important;
      font-style: italic !important;
      margin-bottom: 8px !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .student-details {
      font-family: 'Inter', sans-serif !important;
      font-size: 12px !important;
      color: #64748b !important;
      font-weight: 500 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .completion-text {
      font-family: 'Crimson Text', serif !important;
      font-size: 16px !important;
      color: #374151 !important;
      margin: 12px 0 !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .program-details {
      margin: 12px 0 !important;
    }
    
    .program-title-cert {
      font-family: 'Playfair Display', serif !important;
      font-size: 24px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      margin-bottom: 10px !important;
      text-shadow: none !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .program-category-cert {
      font-family: 'Inter', sans-serif !important;
      font-size: 14px !important;
      color: #64748b !important;
      font-style: italic !important;
      font-weight: 500 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .program-info-grid {
      display: flex !important;
      justify-content: space-around !important;
      margin: 15px 0 !important;
      background: linear-gradient(135deg, #f8fafc, #f1f5f9) !important;
      padding: 15px !important;
      border-radius: 10px !important;
      border: 2px solid #e2e8f0 !important;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
      gap: 15px !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .info-group {
      display: flex !important;
      flex-direction: column !important;
      gap: 6px !important;
      flex: 1 !important;
    }
    
    .info-item {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      padding: 8px !important;
      background: white !important;
      border-radius: 8px !important;
      box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important;
      border: 1px solid #f1f5f9 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .info-label {
      font-family: 'Inter', sans-serif !important;
      font-size: 10px !important;
      color: #64748b !important;
      margin-bottom: 3px !important;
      text-transform: uppercase !important;
      letter-spacing: 1px !important;
      font-weight: 600 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .info-value {
      font-family: 'Inter', sans-serif !important;
      font-size: 12px !important;
      font-weight: 600 !important;
      color: #1e293b !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* ENHANCED GRADE-SPECIFIC COLORS WITH CONSISTENT STYLING */
    .grade-excellent {
      color: #16a34a !important; /* Green color for Excellent */
      font-weight: 700 !important;
      text-shadow: 0 1px 2px rgba(22, 163, 74, 0.2) !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .grade-good {
      color: #2563eb !important; /* Blue color for Good */
      font-weight: 700 !important;
      text-shadow: 0 1px 2px rgba(37, 99, 235, 0.2) !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .grade-poor {
      color: #ea580c !important; /* Light orange color for Poor */
      font-weight: 700 !important;
      text-shadow: 0 1px 2px rgba(234, 88, 12, 0.2) !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .grade-failed {
      color: #dc2626 !important; /* Red color for Failed */
      font-weight: 700 !important;
      text-shadow: 0 1px 2px rgba(220, 38, 38, 0.2) !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .certificate-message {
      font-family: 'Crimson Text', serif !important;
      font-size: 13px !important;
      line-height: 1.5 !important;
      color: #475569 !important;
      margin: 15px 0 !important;
      text-align: justify !important;
      font-style: italic !important;
      padding: 0 15px !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .signature-section {
      display: flex !important;
      justify-content: space-around !important;
      margin: 25px 0 20px 0 !important;
      gap: 30px !important;
    }
    
    .signature-item {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      flex: 1 !important;
    }
    
    .signature-line {
      width: 140px !important;
      height: 2px !important;
      background: #cbd5e1 !important;
      margin-bottom: 6px !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .signature-label {
      font-family: 'Inter', sans-serif !important;
      font-size: 12px !important;
      color: #64748b !important;
      font-weight: 500 !important;
      margin-bottom: 3px !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .signature-name {
      font-family: 'Inter', sans-serif !important;
      font-size: 10px !important;
      color: #94a3b8 !important;
      font-style: italic !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* FOOTER - MOVED HIGHER UP WITH REDUCED MARGINS */
    .certificate-footer-inside {
      background: white !important;
      color: #1e293b !important;
      padding: 10px 20px 25px 20px !important;
      flex-shrink: 0 !important;
      margin-top: 0px !important;
      position: relative !important;
      z-index: 5 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .footer-content {
      text-align: center !important;
    }
    
    .institution-address {
      font-family: 'Inter', sans-serif !important;
      font-size: 11px !important;
      margin-bottom: 5px !important;
      color: #374151 !important;
      line-height: 1.4 !important;
      font-weight: 500 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .footer-note {
      font-family: 'Inter', sans-serif !important;
      font-size: 10px !important;
      line-height: 1.3 !important;
      color: #dc2626 !important;
      margin-bottom: 4px !important;
      font-weight: 600 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .footer-confidential {
      font-family: 'Inter', sans-serif !important;
      font-size: 9px !important;
      line-height: 1.3 !important;
      color: #64748b !important;
      font-weight: 400 !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* PRINT STYLES - PORTRAIT ENFORCED WITH ENHANCED BORDERS AND GRADE COLORS */
    @media print {
      @page {
        size: A4 portrait !important;
        margin: 0.2in !important;
      }
      
      html, body {
        orientation: portrait !important;
        width: 100% !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      .certificate-page {
        height: 100vh !important;
        width: 100% !important;
        background: white !important;
        padding: 2px !important;
        orientation: portrait !important;
      }
      
      .certificate-border {
        /* FORCE BORDER VISIBILITY IN PRINT */
        border: 8px solid #d4af37 !important;
        outline: 2px solid #d4af37 !important;
        outline-offset: -2px !important;
        background-color: white !important;
        background-clip: padding-box !important;
        box-shadow: 
          inset 0 0 0 4px rgba(212, 175, 55, 0.15),
          0 0 0 2px rgba(212, 175, 55, 0.6),
          0 0 0 4px rgba(212, 175, 55, 0.4),
          0 0 0 6px rgba(212, 175, 55, 0.2),
          0 8px 32px rgba(212, 175, 55, 0.3),
          0 12px 48px rgba(0, 0, 0, 0.15) !important;
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        min-height: auto !important;
        orientation: portrait !important;
        border-radius: 20px !important;
        filter: drop-shadow(0 0 15px rgba(212, 175, 55, 0.25)) !important;
      }
      
      .certificate-border::before,
      .certificate-border::after {
        width: 70px !important;
        height: 70px !important;
        border: 3px solid #d4af37 !important;
        background: rgba(212, 175, 55, 0.1) !important;
      }
      
      .certificate-border::before {
        top: 15px !important;
        left: 15px !important;
      }
      
      .certificate-border::after {
        bottom: 15px !important;
        right: 15px !important;
      }
      
      .certificate-header-inside, .certificate-footer-inside, .certificate-main {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
        background: white !important;
      }
      
      .certificate-header-inside {
        color: #1e293b !important;
        padding: 20px 20px 15px 20px !important;
      }
      
      .certificate-footer-inside {
        color: #1e293b !important;
        padding: 8px 15px 20px 15px !important;
        margin-top: 0px !important;
      }
      
      .certificate-main {
        padding: 15px 25px 10px 25px !important;
      }
      
      .institution-name-main {
        color: #1e293b !important;
        font-size: 32px !important;
      }
      
      .certificate-subtitle-header {
        color: #64748b !important;
        font-size: 16px !important;
      }
      
      .certificate-title, .program-title-cert {
        color: #1e293b !important;
      }
      
      .certificate-title {
        font-size: 26px !important;
      }
      
      .program-title-cert {
        font-size: 22px !important;
      }
      
      .student-name-certificate {
        border-bottom: 3px solid #e2e8f0 !important;
        color: #1e293b !important;
        background: white !important;
        font-size: 26px !important;
      }
      
      .signature-line {
        background: #cbd5e1 !important;
      }
      
      .program-info-grid {
        border: 2px solid #e2e8f0 !important;
        background: linear-gradient(135deg, #f8fafc, #f1f5f9) !important;
        margin: 10px 0 !important;
        padding: 10px !important;
      }
      
      .info-item {
        background: white !important;
        padding: 6px !important;
        border: 1px solid #f1f5f9 !important;
      }
      
      /* ENHANCED PRINT GRADE COLORS */
      .grade-excellent {
        color: #16a34a !important; /* Green for Excellent */
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(22, 163, 74, 0.2) !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      .grade-good {
        color: #2563eb !important; /* Blue for Good */
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(37, 99, 235, 0.2) !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      .grade-poor {
        color: #ea580c !important; /* Light orange for Poor */
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(234, 88, 12, 0.2) !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      .grade-failed {
        color: #dc2626 !important; /* Red for Failed */
        font-weight: 700 !important;
        text-shadow: 0 1px 2px rgba(220, 38, 38, 0.2) !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      .program-category-cert {
        color: #64748b !important;
      }
      
      .institution-address, .footer-note, .footer-confidential {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      .institution-address {
        color: #374151 !important;
        font-size: 10px !important;
      }
      
      .footer-note {
        color: #dc2626 !important;
        font-size: 9px !important;
      }
      
      .footer-confidential {
        color: #64748b !important;
        font-size: 8px !important;
      }
      
      .certificate-number-container {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        width: 100% !important;
      }
      
      .certificate-number {
        background: white !important;
        color: #000000 !important;
        text-align: center !important;
        font-size: 10px !important;
        font-weight: 600 !important;
        border: 2px solid #d4d4d8 !important;
        letter-spacing: 0.5px !important;
      }
      
      .generation-date, .student-father-name, .student-details, .completion-text, .certificate-message, .signature-label, .signature-name, .info-label, .info-value {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      .signature-section {
        margin: 12px 0 8px 0 !important;
      }
      
      .signature-line {
        width: 120px !important;
        margin-bottom: 4px !important;
      }
      
      .certificate-message {
        font-size: 12px !important;
        margin: 10px 0 !important;
      }
    }
  `;
};
